import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';
import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchUsersReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { ReportApiDateRangeQuery, UserReportApiItem } from '../../types/report';
import { gridFormatDecimalString } from './reportGridFormatters';
import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

function usersReportRowId(row: UserReportApiItem) {
  return String(row.user_id);
}

export function UsersReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);
  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'user_id', headerName: 'User Id', type: 'number', width: 90 },
    { field: 'user_name', headerName: 'Name', minWidth: 160, flex: 0.5 },
    { field: 'user_email', headerName: 'Email', minWidth: 200, flex: 0.5 },
    { field: 'user_phone', headerName: 'Phone', width: 140 },
    { field: 'age', headerName: 'Age', width: 70 },
    { field: 'register_date', headerName: 'Register date', type: 'dateTime', width: 180 },
    { field: 'last_activity_date', headerName: 'Last activity', type: 'dateTime', width: 180 },
    { field: 'nationality', headerName: 'Nationality', width: 130 },
    { field: 'status', headerName: 'Status', width: 110 },
    { field: 'campaign_attribution', headerName: 'Campaign', width: 120 },
    {
      field: 'net_redemptions_amount',
      headerName: 'Net redemptions',
      width: 140,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'net_subscriptions_amount',
      headerName: 'Net subscriptions',
      width: 150,
      valueFormatter: gridFormatDecimalString,
    },
    {
      field: 'market_portfolio_size',
      headerName: 'Market portfolio size',
      width: 150,
      valueFormatter: gridFormatDecimalString,
    },
    { field: 'survey_score', headerName: 'Survey score', width: 120, valueFormatter: gridFormatDecimalString },
    { field: 'risk_profile', headerName: 'Risk profile', width: 130 },
    { field: 'first_investment_date', headerName: 'First investment', type: 'dateTime', width: 180 },
    { field: 'last_investment_date', headerName: 'Last investment', type: 'dateTime', width: 180 },
    { field: 'customer_company', headerName: 'Company', minWidth: 120, flex: 0.3 },
    { field: 'address_or_postal_code', headerName: 'Address / postal', minWidth: 140, flex: 0.3 },
    { field: 'number_of_subscription_orders', headerName: 'Sub. orders', width: 110 },
    { field: 'number_of_redemption_orders', headerName: 'Redemption orders', width: 140 },
  ]);

  const { usersReportItems, totalUsersReportItems, fetchUsersReportStatus, error } = useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    'users-report.csv',
    useCallback(() => reportService.exportUsersReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}
      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">Users</Typography>
          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>
        <Table
          wrapperHeight={700}
          fetchItems={fetchUsersReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={usersReportItems}
          columns={columns.current}
          getRowId={usersReportRowId}
          loading={fetchUsersReportStatus === fetchStatuses.pending}
          rowCount={totalUsersReportItems}
          initialSortModel={[{ field: 'user_id', sort: 'asc' }]}
        />
      </Grid>
    </>
  );
}
